/**
 * Client-side video thumbnail extraction (first frame via canvas).
 *
 * Used by the direct-to-GCS video upload path: the video file is already in
 * the browser, so the first frame is grabbed locally instead of shipping the
 * whole file through the server-side ffmpeg pipeline. The extracted PNG
 * (~100KB) is PUT to a signed URL alongside the video.
 *
 * NEVER throws — a codec the browser cannot decode resolves to `null`
 * (such a video would not play in the funnel runtime either, and the
 * AssetUrl thumb fields fall back to the video URL server-side).
 *
 * Node/CLI note: this module requires DOM APIs (<video>, canvas,
 * URL.createObjectURL). A future CLI `assets upload-video` command must
 * extract its own thumbnail (e.g. ffmpeg-static) or send hasThumbnail=false
 * — the /api/assets/upload-url contract already supports both.
 */

/** Seek target: slightly past 0 so we skip a potential black first frame. */
const SEEK_SECONDS = 0.1;

/** Give up on metadata/seek/decode after this long (broken or huge headers). */
const EXTRACTION_TIMEOUT_MS = 15_000;

/**
 * Extract the first frame of a video file as a PNG blob.
 *
 * @param file - Video file selected by the user
 * @param targetWidth - Output width in px (height keeps aspect ratio);
 *   matches the 200px default of the server-side ffmpeg pipeline
 * @returns PNG blob, or `null` when the browser cannot decode the file
 */
export async function extractVideoThumbnail(
  file: File,
  targetWidth: number = 200
): Promise<Blob | null> {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return null; // non-DOM environment (jest node env, SSR)
  }

  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true; // required for programmatic playback/seek in some browsers
  video.playsInline = true;

  try {
    const frameReady = new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), EXTRACTION_TIMEOUT_MS);
      const fail = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      video.onerror = fail;
      video.onloadedmetadata = () => {
        // Seek slightly forward; 'seeked' fires when the frame is decoded.
        try {
          video.currentTime = Math.min(SEEK_SECONDS, video.duration || SEEK_SECONDS);
        } catch {
          fail();
        }
      };
      video.onseeked = () => {
        clearTimeout(timeout);
        resolve(true);
      };
    });

    video.src = objectUrl;
    const ready = await frameReady;

    if (!ready || !video.videoWidth || !video.videoHeight) {
      return null;
    }

    const scale = targetWidth / video.videoWidth;
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  } catch {
    return null;
  } finally {
    // Release the blob URL and detach the decoder.
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}
